import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  HeartPulse, CheckCircle2, Pill, Stethoscope, 
  Settings, Users, Loader2
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { db } from '../Firebase';
import { collection, onSnapshot } from 'firebase/firestore';

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [highlightServiceId, setHighlightServiceId] = useState(null);

  const location = useLocation();

  useEffect(() => {
    if (location.state && location.state.selectedServiceId) {
      setHighlightServiceId(location.state.selectedServiceId);
    }
  }, [location.state]);

  useEffect(() => {
    if (highlightServiceId && services.length > 0) {
      const timer = setTimeout(() => {
        const element = document.getElementById(`service-card-${highlightServiceId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [highlightServiceId, services]);

  useEffect(() => {
    const servicesRef = collection(db, 'services');

    const unsubscribe = onSnapshot(servicesRef, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const unique = [];
      const seen = new Set();
      for (const s of list) {
        if (s.title) {
          const norm = s.title.trim().toLowerCase();
          if (!seen.has(norm)) {
            seen.add(norm);
            unique.push(s);
          }
        }
      }
      setServices(unique);
      setLoading(false);
    }, (err) => {
      console.error("Error loading services:", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className="font-sans bg-brand-light">
      
      {/* Page Header */}
      <section className="bg-brand-navy text-white pt-28 pb-16 relative overflow-hidden grid-mesh">
        <div className="absolute inset-0 bg-brand-navy/80 z-10" />
        <div className="absolute inset-0 z-0 bg-brand-navy/40" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20 text-center space-y-4">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-green font-poppins block">Our Solutions</span>
          <h1 className="text-4xl sm:text-5xl font-extrabold font-montserrat text-white tracking-tight">
            Specialized Healthcare Services
          </h1>
          <p className="text-sm text-brand-silver max-w-2xl mx-auto font-poppins">
            Delivering surgical and pharmaceutical distribution pipelines alongside FDA & ISO compliant turnkey hospital setups.
          </p>
        </div>
      </section>

      {/* Services Detail List */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400 space-y-4">
              <Loader2 className="animate-spin text-brand-blue" size={40} />
              <p className="text-sm font-semibold font-poppins">Loading specialized services...</p>
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-20 text-gray-400">No services catalog setup. Please configure in administration panel.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((svc, idx) => {
                const IconComponent = LucideIcons[svc.icon] || LucideIcons.Settings;
                const isHighlighted = highlightServiceId === svc.id;
                return (
                  <div 
                    key={idx} 
                    id={`service-card-${svc.id}`}
                    className={`flex flex-col h-full bg-white rounded-3xl transition-all duration-500 border group overflow-hidden ${
                      isHighlighted 
                        ? 'border-2 border-brand-blue shadow-2xl ring-4 ring-brand-blue/15 scale-[1.02] -translate-y-1' 
                        : 'border-brand-silver/50 shadow-md hover:shadow-2xl hover:border-brand-blue/30 hover:-translate-y-1.5'
                    }`}
                  >
                    {/* Image / Gradient Header */}
                    <div className="h-52 bg-brand-light relative overflow-hidden shrink-0">
                      {svc.image ? (
                        <img 
                          src={svc.image} 
                          alt={svc.title} 
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-brand-navy to-brand-blue relative flex flex-col justify-center items-center p-8">
                          <div className="absolute inset-0 bg-grid-mesh opacity-10" />
                          <div className="absolute -top-12 -right-12 w-36 h-36 bg-brand-green/20 rounded-full blur-2xl group-hover:bg-brand-blue/30 transition-all duration-500" />
                        </div>
                      )}
                      
                      {/* Floating Icon */}
                      <div className={`absolute top-4 right-4 w-12 h-12 rounded-xl backdrop-blur-sm flex items-center justify-center border transition-all duration-300 ${
                        isHighlighted 
                          ? 'bg-brand-blue text-white border-brand-blue/20' 
                          : 'bg-white/95 text-brand-blue border-brand-silver/30 group-hover:bg-brand-blue group-hover:text-white'
                      }`}>
                        <IconComponent size={20} className="stroke-[2.5] transition-transform duration-300 group-hover:scale-110" />
                      </div>
                    </div>

                    {/* Card Content Area */}
                    <div className="p-6 flex-grow flex flex-col justify-between space-y-6">
                      <div className="space-y-4">
                        <h3 className={`text-xl font-bold font-montserrat tracking-tight leading-tight transition-colors duration-300 ${
                          isHighlighted ? 'text-brand-blue' : 'text-brand-navy group-hover:text-brand-blue'
                        }`}>
                          {svc.title}
                        </h3>

                        <p className="text-sm text-gray-500 font-poppins leading-relaxed font-light line-clamp-4">
                          {svc.desc}
                        </p>

                        {/* Highlights */}
                        <div className="space-y-2.5 pt-4 border-t border-brand-silver/40">
                          <h4 className="text-xs font-bold font-montserrat uppercase tracking-wider text-brand-navy flex items-center gap-1.5">
                            <span className="w-1 h-3.5 bg-brand-blue rounded-full animate-pulse-slow" />
                            Key Highlights
                          </h4>
                          <ul className="space-y-2 text-xs text-gray-500 font-poppins">
                            {svc.points?.map((pt, pIdx) => (
                              <li key={pIdx} className="flex gap-2 items-start hover:text-brand-navy transition-colors duration-200">
                                <CheckCircle2 size={13} className="text-brand-green shrink-0 mt-0.5" />
                                <span className="leading-snug">{pt}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Primary Installations / Outlets */}
                      <div className="bg-brand-light p-3.5 rounded-xl border border-brand-silver/50 text-xs font-semibold font-poppins space-y-1 mt-auto">
                        <div className="text-gray-400 text-[10px] uppercase tracking-wider font-bold">Primary installations</div>
                        <div className="text-brand-navy truncate" title={svc.setup}>{svc.setup}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Target Industries Served */}
      <section className="py-20 bg-brand-light border-y border-brand-silver/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-blue font-poppins block">Ecosystem</span>
            <h2 className="text-3xl font-bold font-montserrat text-brand-navy">Industries We Partner With</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { 
                name: 'Medical Stores & Pharmacies', 
                icon: Pill,
                desc: 'Secure daily pharmaceutical delivery pipelines carrying FDA verified essential medicines, vaccines, and surgical consumables.' 
              },
              { 
                name: 'Hospitals & Nursing Homes', 
                icon: Stethoscope,
                desc: 'Full critical care infrastructure setups, certified surgical pendants, fowler/motorized beds, shadows-free surgical lights, and copper piping systems.' 
              },
              { 
                name: 'Healthcare Institutes & Laboratories', 
                icon: Settings,
                desc: 'Compliant clinical simulation setups, heavy anatomical layouts, procedure chairs, diagnostic lights, and specialized diagnostic tables.' 
              }
            ].map((ind, idx) => {
              const IndustryIcon = ind.icon;
              return (
                <div 
                  key={idx} 
                  className="bg-white p-8 rounded-2xl border border-brand-silver/30 shadow-sm space-y-5 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 h-1.5 w-full bg-brand-silver group-hover:bg-brand-blue transition-colors duration-300" />
                  <div className="w-12 h-12 rounded-xl bg-brand-light group-hover:bg-brand-blue/10 text-brand-navy group-hover:text-brand-blue flex items-center justify-center border border-brand-silver/40 shadow-inner transition-colors duration-300">
                    <IndustryIcon size={22} className="stroke-[2] transition-transform duration-300 group-hover:scale-110" />
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-extrabold text-lg text-brand-navy font-montserrat group-hover:text-brand-blue transition-colors duration-300">
                      {ind.name}
                    </h4>
                    <p className="text-sm text-gray-500 font-poppins leading-relaxed">{ind.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Clinical End Users Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-brand-navy text-white rounded-3xl p-8 sm:p-16 grid-mesh relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-brand-blue/10 rounded-full blur-[80px]" />
            <div className="relative z-10 text-center max-w-3xl mx-auto space-y-8">
              <span className="text-xs font-bold uppercase tracking-widest text-brand-green font-poppins block">End Users</span>
              <h3 className="text-3xl font-extrabold font-montserrat text-white tracking-tight">Optimizing Care for End Users</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 text-left">
                <div className="space-y-3 bg-white/5 p-6 rounded-2xl border border-white/5 backdrop-blur-md hover:bg-white/10 transition-colors duration-300">
                  <h4 className="font-bold text-lg text-brand-green font-montserrat flex items-center gap-2">
                    <HeartPulse size={20} className="text-brand-green" />
                    Patients
                  </h4>
                  <p className="text-sm text-brand-silver font-poppins leading-relaxed">
                    By procuring robust, ergonomic manual/motorized recovery beds, silent ventilation systems, antibacterial curtains, and genuine medication, we ensure absolute comfort and reduce post-surgery infection rates for recovering patients.
                  </p>
                </div>
                <div className="space-y-3 bg-white/5 p-6 rounded-2xl border border-white/5 backdrop-blur-md hover:bg-white/10 transition-colors duration-300">
                  <h4 className="font-bold text-lg text-brand-blue font-montserrat flex items-center gap-2">
                    <Users size={20} className="text-brand-blue" />
                    Healthcare Providers
                  </h4>
                  <p className="text-sm text-brand-silver font-poppins leading-relaxed">
                    We deliver highly ergonomic ward layouts, double-arm surgical pendants, shadowsless LED systems, and centrally accessible cardiac consoles that minimize response delays, eliminate doctor fatigue, and enhance facility performance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
